import LoadingButton from "@mui/lab/LoadingButton"
import { useFormikContext } from "formik"
import { ArrowLeft, X } from "lucide-mui"
import React from "react"

import i18n from "@/i18n"
import Logo from "@/pages/public/logo"
import { type ReservationFormValues } from "@/pages/public/venue"

import { STYLES as SHARED_STYLES } from "../_styles"

import BookingSummaryCard from "./booking_summary_card"
import { type GuestInformationStepProps, GuestFormFields } from "./shared"

const STYLES = {
  ...SHARED_STYLES,
  rootContainer: "flex h-full min-h-0 flex-col",
  holdDuration: "bg-[#f5f7f9] px-5 py-2 text-center text-[10px] leading-[14px] text-[#6b7280]",
  holdDurationStrong: "font-semibold text-[#5f6774]",
  formContainer: "space-y-5 px-5 py-5",
} as const

const GuestInformationStepMobile: React.FC<GuestInformationStepProps> = ({
  holdDurationSeconds,
  country,
  timezone,
  tables = [],
  mobileSheet = true,
  onBack,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const { values } = useFormikContext<ReservationFormValues>()
  const holdDurationMinutes = Math.max(1, Math.round((holdDurationSeconds ?? 5 * 60) / 60))
  const isValid = values.name && values.email && values.phone && values.termsAccepted
  const effectiveOnClose = onClose || onBack
  const titleText = i18n.t("app.reservation_preview.buttons.more")

  return (
    <div className={STYLES.rootContainer}>
      <div className={STYLES.mobileSheetHeader}>
        <button
          type="button"
          onClick={onBack}
          className={STYLES.mobileSheetBackBtn}
          aria-label={i18n.t("app.public.venue.modal.back")}
          data-testid="mobile-sheet-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className={STYLES.mobileSheetTitle}>{titleText}</h2>
        <button
          type="button"
          onClick={effectiveOnClose}
          className={STYLES.mobileSheetCloseBtn}
          aria-label={i18n.t("app.public.venue.modal.close")}
          data-testid="mobile-sheet-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className={STYLES.holdDuration} data-testid="public-reservation-modal-guest-hold-duration">
        <span>{`${i18n.t("app.public.venue.modal.hold_duration_prefix")} `}</span>
        <strong className={STYLES.holdDurationStrong}>
          {`${holdDurationMinutes} ${i18n.t("app.public.venue.modal.minutes")}`}
        </strong>
        <span>.</span>
      </div>

      <div className={STYLES.mobileSheetContent}>
        <div className={STYLES.formContainer} data-testid="public-reservation-modal-guest-container">
          <BookingSummaryCard values={values} timezone={timezone} tables={tables} />

          <GuestFormFields mobileSheet={mobileSheet} country={country} />
        </div>
      </div>

      <div className={STYLES.mobileSheetFooter}>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          disabled={!isValid}
          loading={isSubmitting}
          className={STYLES.mobileSheetNextBtn}
          data-testid="mobile-sheet-next-btn"
          sx={{
            backgroundColor: "#2596fa",
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 500,
            height: "40px",
            "&:hover": { backgroundColor: "#1e7cd8" },
            "&.Mui-disabled": { backgroundColor: "#2596fa", opacity: 0.5 },
          }}
        >
          {i18n.t("app.public.venue.modal.reserve_table")}
        </LoadingButton>
        <div className="mt-3 flex justify-center">
          <Logo variant="wordmark" className="h-[10px] w-auto" aria-label="tabb" />
        </div>
      </div>
    </div>
  )
}

export default GuestInformationStepMobile
